import { FormEvent, useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import {
  cancelEmailChange,
  getProfile,
  requestEmailChange,
  resendEmailChange,
  verifyAccountPassword,
  type ProfileResponse,
} from "~/api/auth"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

type EmailStep = "idle" | "password" | "new_email"

export function AccountDetailsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [emailStep, setEmailStep] = useState<EmailStep>("idle")
  const [currentPassword, setCurrentPassword] = useState("")
  const [newEmail, setNewEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data)
        if (searchParams.get("email_changed") === "1") {
          setMessage("Email updated successfully.")
        }
        if (searchParams.get("email_change") === "invalid") {
          setError("That email change link is invalid or has expired.")
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate, searchParams])

  const pending = profile?.user.pending_email_change

  async function handleStartEmailChange(event: FormEvent) {
    event.preventDefault()
    setError(null)
    if (emailStep === "password") {
      if (!currentPassword.trim()) {
        setError("Current password is required")
        return
      }
      setBusy(true)
      try {
        await verifyAccountPassword(currentPassword)
        setEmailStep("new_email")
      } catch (err) {
        setError(err instanceof Error ? err.message : "Current password is incorrect")
      } finally {
        setBusy(false)
      }
      return
    }

    setBusy(true)
    try {
      const updated = await requestEmailChange(currentPassword, newEmail.trim())
      setProfile(updated)
      setEmailStep("idle")
      setCurrentPassword("")
      setNewEmail("")
      setMessage("Verification email sent. Your current email remains active until you confirm.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start email change")
    } finally {
      setBusy(false)
    }
  }

  async function handleResend() {
    setError(null)
    setBusy(true)
    try {
      const updated = await resendEmailChange()
      setProfile(updated)
      setMessage("Verification email resent.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend")
    } finally {
      setBusy(false)
    }
  }

  async function handleCancelPending() {
    setError(null)
    setBusy(true)
    try {
      const updated = await cancelEmailChange()
      setProfile(updated)
      setMessage("Email change cancelled.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not cancel")
    } finally {
      setBusy(false)
    }
  }

  if (loading || !profile) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="w-full p-2">
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">Account Details</h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            View your email and manage account security.
          </p>

          <dl className="mt-6 space-y-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
                Email
              </dt>
              <dd className="mt-1 text-sm text-[var(--color-text-primary)]">{profile.user.email}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
                Verification
              </dt>
              <dd className="mt-1 text-sm text-[var(--color-text-primary)]">
                {profile.user.email_verified ? "Verified" : "Not verified"}
              </dd>
            </div>
          </dl>

          {pending ? (
            <div className="mt-6 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4">
              <p className="text-sm text-[var(--color-text-primary)]">
                Verification pending for <strong>{pending.new_email}</strong>. Your current email
                remains your login until you confirm.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleResend()}
                  className="rounded-lg border border-[var(--color-divider)] px-3 py-2 text-sm font-medium disabled:opacity-50"
                >
                  Resend
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleCancelPending()}
                  className="rounded-lg border border-[var(--color-divider)] px-3 py-2 text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : emailStep === "idle" ? (
            <button
              type="button"
              onClick={() => {
                setEmailStep("password")
                setError(null)
                setMessage(null)
              }}
              className="mt-6 inline-flex rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
            >
              Change email
            </button>
          ) : (
            <form onSubmit={handleStartEmailChange} className="mt-6 space-y-4">
              {emailStep === "password" && (
                <label className="block text-sm text-[var(--color-text-primary)]">
                  Current password
                  <input
                    type="password"
                    autoComplete="current-password"
                    aria-label="Current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2"
                  />
                </label>
              )}
              {emailStep === "new_email" && (
                <label className="block text-sm text-[var(--color-text-primary)]">
                  New email
                  <input
                    type="email"
                    autoComplete="email"
                    aria-label="New email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    className="mt-1 w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-3 py-2"
                  />
                </label>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
                >
                  {emailStep === "password"
                    ? busy
                      ? "Checking…"
                      : "Continue"
                    : busy
                      ? "Sending…"
                      : "Send verification"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEmailStep("idle")
                    setCurrentPassword("")
                    setNewEmail("")
                    setError(null)
                  }}
                  className="rounded-lg border border-[var(--color-divider)] px-4 py-2 text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {error && (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="mt-4 text-sm text-green-700" role="status">
              {message}
            </p>
          )}

          <Link
            to="/change-password"
            className="mt-6 inline-flex rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90"
          >
            Change Password
          </Link>

          <Link
            to="/profile"
            className="mt-6 block text-sm font-medium text-[var(--color-text-primary)] underline"
          >
            Back to Profile
          </Link>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
